import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Upload, Loader2, BarChart3, Clock, Users, FileCheck, Target,
  AlertTriangle, CheckCircle, Lightbulb, Activity, Layers
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PMIntegrationView = () => {
  const [activeTab, setActiveTab] = useState("import");
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [resourceResult, setResourceResult] = useState(null);

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/pm-integration/schedules`);
      setSchedules(res.data.schedules || []);
    } catch (e) { console.log("No schedules"); }
  };

  const handleImport = async (e, source) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const endpoint = source === "p6" ? "/pm-integration/import/p6" : "/pm-integration/import/ms-project";
      const res = await axios.post(`${API}${endpoint}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.total_activities} activities from ${source === "p6" ? "Primavera P6" : "MS Project"}`);
      fetchSchedules();
      setActiveTab("schedules");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  const viewSchedule = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/pm-integration/schedules/${scheduleId}`);
      setSelectedSchedule(res.data);
      setActiveTab("detail");
    } catch (e) { toast.error("Failed to load schedule"); }
  };

  const runAIAnalysis = async (scheduleId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pm-integration/schedules/${scheduleId}/ai-analyze`);
      setAnalysisResult(res.data);
      setActiveTab("analysis");
      toast.success("Schedule analysis complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setLoading(false); }
  };

  const runResourceLeveling = async (scheduleId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pm-integration/schedules/${scheduleId}/resource-leveling`);
      setResourceResult(res.data);
      toast.success("Resource leveling complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Resource leveling failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="pm-integration-view">
      <div>
        <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">PM Tool Integration</h1>
        <p className="text-zinc-500 mt-1">Import and analyze schedules from Primavera P6 and MS Project</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 border-2 border-zinc-200">
          <TabsTrigger value="import" data-testid="tab-import"><Upload size={14} className="mr-1" /> Import</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="tab-schedules"><Layers size={14} className="mr-1" /> Schedules ({schedules.length})</TabsTrigger>
          {selectedSchedule && <TabsTrigger value="detail" data-testid="tab-detail"><FileCheck size={14} className="mr-1" /> Detail</TabsTrigger>}
          {analysisResult && <TabsTrigger value="analysis" data-testid="tab-analysis"><BarChart3 size={14} className="mr-1" /> Analysis</TabsTrigger>}
        </TabsList>

        <TabsContent value="import">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border-2 border-black brutal-shadow bg-white p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mx-auto">
                <Activity size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-bold font-['Chivo']">Primavera P6</h3>
              <p className="text-sm text-zinc-500">Import schedule from Oracle Primavera P6 (XML or XER)</p>
              <label className="block">
                <input type="file" accept=".xml,.xer" onChange={e => handleImport(e, "p6")} className="hidden" data-testid="p6-file-input" />
                <Button asChild disabled={loading} className="bg-[#FF4F00] text-white w-full cursor-pointer">
                  <span>{loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing...</> : <><Upload size={16} className="mr-2" /> Upload P6 File</>}</span>
                </Button>
              </label>
              <p className="text-xs text-zinc-400">Supports .xml and .xer formats (all P6 versions)</p>
            </div>

            <div className="border-2 border-black brutal-shadow bg-white p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto">
                <Target size={32} className="text-white" />
              </div>
              <h3 className="text-lg font-bold font-['Chivo']">MS Project</h3>
              <p className="text-sm text-zinc-500">Import schedule from Microsoft Project (XML or MPP)</p>
              <label className="block">
                <input type="file" accept=".xml,.mpp" onChange={e => handleImport(e, "msproject")} className="hidden" data-testid="msp-file-input" />
                <Button asChild disabled={loading} className="bg-[#0047AB] text-white w-full cursor-pointer">
                  <span>{loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing...</> : <><Upload size={16} className="mr-2" /> Upload MS Project File</>}</span>
                </Button>
              </label>
              <p className="text-xs text-zinc-400">Supports .xml and .mpp formats (all versions)</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="schedules">
          <div className="border-2 border-black brutal-shadow bg-white overflow-hidden">
            {schedules.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">
                <Layers size={48} className="mx-auto mb-4 opacity-30" />
                <p>No schedules imported yet. Import a P6 or MS Project file to get started.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b-2 border-zinc-200">
                  <tr>
                    <th className="text-left p-3 font-bold text-xs uppercase">Project</th>
                    <th className="text-left p-3 font-bold text-xs uppercase">Source</th>
                    <th className="text-left p-3 font-bold text-xs uppercase">Activities</th>
                    <th className="text-left p-3 font-bold text-xs uppercase">Resources</th>
                    <th className="text-left p-3 font-bold text-xs uppercase">Critical</th>
                    <th className="text-left p-3 font-bold text-xs uppercase">Imported</th>
                    <th className="text-left p-3 font-bold text-xs uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((s, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3 font-medium">{s.project_name}</td>
                      <td className="p-3"><Badge variant="outline">{s.source === "primavera_p6" ? "P6" : "MS Project"}</Badge></td>
                      <td className="p-3">{s.total_activities}</td>
                      <td className="p-3">{s.total_resources}</td>
                      <td className="p-3"><span className="text-red-600 font-bold">{s.critical_activities}</span></td>
                      <td className="p-3 text-xs text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewSchedule(s.id)} data-testid={`view-schedule-${i}`}>View</Button>
                          <Button size="sm" className="bg-black text-white" onClick={() => runAIAnalysis(s.id)} disabled={loading} data-testid={`analyze-schedule-${i}`}>
                            {loading ? <Loader2 size={14} className="animate-spin" /> : "AI Analyze"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {selectedSchedule && (
          <TabsContent value="detail">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-['Chivo']">{selectedSchedule.project_name}</h2>
                  <p className="text-sm text-zinc-500">Source: {selectedSchedule.source} | File: {selectedSchedule.filename}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => runAIAnalysis(selectedSchedule.id)} disabled={loading} className="bg-[#0047AB] text-white" data-testid="detail-ai-analyze">
                    {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <BarChart3 size={14} className="mr-1" />} AI Analyze
                  </Button>
                  <Button onClick={() => runResourceLeveling(selectedSchedule.id)} disabled={loading} variant="outline" data-testid="detail-resource-level">
                    <Users size={14} className="mr-1" /> Resource Leveling
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Activities</p>
                  <p className="text-2xl font-black">{selectedSchedule.total_activities}</p>
                </div>
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Resources</p>
                  <p className="text-2xl font-black text-[#0047AB]">{selectedSchedule.total_resources}</p>
                </div>
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Critical Path</p>
                  <p className="text-2xl font-black text-[#FF4F00]">{selectedSchedule.critical_activities}</p>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-bold uppercase text-zinc-500 mb-3">Activities ({selectedSchedule.activities?.length || 0})</h3>
                <ScrollArea className="h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-xs">ID</th>
                        <th className="text-left p-2 text-xs">Name</th>
                        <th className="text-left p-2 text-xs">Duration</th>
                        <th className="text-left p-2 text-xs">Progress</th>
                        <th className="text-left p-2 text-xs">Critical</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedSchedule.activities || []).map((a, i) => (
                        <tr key={i} className={`border-b border-zinc-100 ${a.is_critical ? 'bg-red-50' : ''}`}>
                          <td className="p-2 text-xs font-mono">{a.activity_id}</td>
                          <td className="p-2 font-medium text-xs">{a.name}</td>
                          <td className="p-2 text-xs">{a.duration_days}d</td>
                          <td className="p-2 text-xs">{a.percent_complete}%</td>
                          <td className="p-2">{a.is_critical && <Badge className="bg-red-100 text-red-700 border-none text-xs">Critical</Badge>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>

              {resourceResult && (
                <div className="mt-6 pt-6 border-t-2 border-zinc-200 space-y-4">
                  <h3 className="text-sm font-bold">Resource Leveling Results</h3>
                  <div className="p-4 bg-blue-50 border border-blue-200 text-center">
                    <p className="text-xs font-bold uppercase text-blue-600">Leveling Score</p>
                    <p className="text-3xl font-black text-[#0047AB]">{resourceResult.leveling_score}/100</p>
                  </div>
                  {resourceResult.conflicts && resourceResult.conflicts.map((c, i) => (
                    <div key={i} className="p-3 border border-amber-200 bg-amber-50">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle size={14} className="text-amber-600" />
                        <span className="font-medium text-sm">{c.resource}</span>
                      </div>
                      <p className="text-xs text-zinc-600">{c.resolution}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {analysisResult && (
          <TabsContent value="analysis">
            <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
              <h2 className="text-lg font-bold font-['Chivo']">AI Schedule Analysis</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-zinc-50 border text-center">
                  <p className="text-xs font-bold uppercase text-zinc-500">Health Score</p>
                  <p className={`text-3xl font-black ${(analysisResult.schedule_health_score || 0) > 70 ? 'text-emerald-600' : 'text-red-600'}`}>{analysisResult.schedule_health_score}</p>
                </div>
                {analysisResult.progress_assessment && (
                  <>
                    <div className="p-4 bg-zinc-50 border text-center">
                      <p className="text-xs font-bold uppercase text-zinc-500">Progress</p>
                      <p className="text-3xl font-black">{((analysisResult.progress_assessment.overall_progress || 0) * 100).toFixed(0)}%</p>
                    </div>
                    <div className="p-4 bg-red-50 border border-red-200 text-center">
                      <p className="text-xs font-bold uppercase text-red-600">Behind Schedule</p>
                      <p className="text-3xl font-black text-red-600">{analysisResult.progress_assessment.behind_schedule_activities}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 border border-emerald-200 text-center">
                      <p className="text-xs font-bold uppercase text-emerald-600">Ahead</p>
                      <p className="text-3xl font-black text-emerald-600">{analysisResult.progress_assessment.ahead_schedule_activities}</p>
                    </div>
                  </>
                )}
              </div>

              {analysisResult.schedule_risks && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase text-zinc-500">Schedule Risks</h3>
                  {analysisResult.schedule_risks.map((r, i) => (
                    <div key={i} className="p-3 border border-zinc-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{r.risk}</span>
                        <Badge className={`border-none text-xs ${r.probability === 'high' ? 'bg-red-100 text-red-700' : r.probability === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{r.probability}</Badge>
                      </div>
                      <p className="text-xs text-zinc-500">{r.mitigation}</p>
                    </div>
                  ))}
                </div>
              )}

              {analysisResult.optimization_opportunities && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase text-zinc-500">Optimization Opportunities</h3>
                  {analysisResult.optimization_opportunities.map((o, i) => (
                    <div key={i} className="p-3 border border-emerald-200 bg-emerald-50 flex items-center gap-3">
                      <Lightbulb size={16} className="text-emerald-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm">{o.opportunity}</p>
                        <p className="text-xs text-zinc-500 mt-1">Potential: {o.potential_saving_days}d saved | Effort: {o.effort_required}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {analysisResult.recommendations && (
                <div className="p-4 bg-blue-50 border border-blue-200">
                  <h3 className="text-xs font-bold uppercase text-blue-600 mb-2">Recommendations</h3>
                  <ul className="space-y-1">
                    {analysisResult.recommendations.map((r, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default PMIntegrationView;
